import { graphqlRequest } from "../graphqlClient";
import type {
  ApprovalRule,
  WorkflowDefinition,
  WorkflowStage,
  WorkflowStatus,
} from "../mappers/approvalWorkflowMapper";
import type {
  ApprovalWorkflowKpiViewConnection,
  ApprovalWorkflowKpiViewNode,
  ApprovalWorkflowStageViewConnection,
  ApprovalWorkflowStageViewFilterInput,
  ApprovalWorkflowStageViewNode,
  ApprovalWorkflowUserViewConnection,
  ApprovalWorkflowUserViewFilterInput,
  ApprovalWorkflowViewConnection,
  ApprovalWorkflowViewFilterInput,
  ApprovalWorkflowViewNode,
  WorkflowBlockingScenarioViewConnection,
  WorkflowBlockingScenarioViewFilterInput,
} from "../models/approvalWorkflowView";

interface WorkflowListResponse {
  approvalWorkflows: {
    approvalWorkflowViewList: ApprovalWorkflowViewConnection;
  };
}

interface StageListResponse {
  approvalWorkflows: {
    approvalWorkflowStageViewList: ApprovalWorkflowStageViewConnection;
  };
}

interface KpiListResponse {
  approvalWorkflows: {
    approvalWorkflowKpiViewList: ApprovalWorkflowKpiViewConnection;
  };
}

interface ApproverListResponse {
  approvalWorkflows: {
    approvalWorkflowUserViewList: ApprovalWorkflowUserViewConnection;
  };
}

interface BlockingScenarioListResponse {
  approvalWorkflows: {
    workflowBlockingScenarioViewList: WorkflowBlockingScenarioViewConnection;
  };
}

const WORKFLOW_LIST_QUERY = `
  query ApprovalWorkflowViewList($filter: ApprovalWorkflowViewFilterInput, $first: Int) {
    approvalWorkflows {
      approvalWorkflowViewList(filter: $filter, first: $first) {
        edges {
          cursor
          node {
            id
            name
            description
            isDefault
            isActive
            isSuspended
            allowSelfApproval
            notifyDeactivateActionsCreated
            scenarioCategory
            minNetImpact
            maxNetImpact
            stageCount
          }
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
      }
    }
  }
`;

const STAGE_LIST_QUERY = `
  query ApprovalWorkflowStageViewList($filter: ApprovalWorkflowStageViewFilterInput, $first: Int) {
    approvalWorkflows {
      approvalWorkflowStageViewList(filter: $filter, first: $first) {
        edges {
          cursor
          node {
            stageId
            approvalWorkflowId
            name
            stageOrder
            approvalRule
            slaDays
            escalateToUserId
            escalateToName
            assigneeId
            assigneeUserId
            assigneeName
            assigneeEmail
          }
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
      }
    }
  }
`;

const KPI_LIST_QUERY = `
  query ApprovalWorkflowKpiViewList {
    approvalWorkflows {
      approvalWorkflowKpiViewList(first: 1) {
        edges {
          cursor
          node {
            activeWorkflows
            totalStages
            namedApprovers
          }
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
      }
    }
  }
`;

const APPROVER_LIST_QUERY = `
  query ApprovalWorkflowUserViewList($filter: ApprovalWorkflowUserViewFilterInput, $first: Int) {
    approvalWorkflows {
      approvalWorkflowUserViewList(filter: $filter, first: $first) {
        edges {
          cursor
          node {
            userId
            name
            email
            roleName
          }
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
      }
    }
  }
`;

const BLOCKING_SCENARIO_LIST_QUERY = `
  query WorkflowBlockingScenarioViewList($filter: WorkflowBlockingScenarioViewFilterInput, $first: Int) {
    approvalWorkflows {
      workflowBlockingScenarioViewList(filter: $filter, first: $first) {
        edges {
          cursor
          node {
            approvalWorkflowId
            scenarioId
            name
            createdByUserId
            createdByUserName
          }
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
      }
    }
  }
`;

/** Approval workflow headers with routing rules and stage counts (vw_ApprovalWorkflows). */
export async function getApprovalWorkflowViewsGraphql(
  filter?: ApprovalWorkflowViewFilterInput,
  first = 200,
): Promise<ApprovalWorkflowViewNode[]> {
  const data = await graphqlRequest<WorkflowListResponse>(WORKFLOW_LIST_QUERY, {
    filter,
    first,
  });
  return data.approvalWorkflows.approvalWorkflowViewList.edges.map(
    (e) => e.node,
  );
}

/** Workflow stages with their assignees (vw_ApprovalWorkflowStages), one row per assignee. */
export async function getApprovalWorkflowStageViewsGraphql(
  workflowId?: number,
  first = 500,
): Promise<ApprovalWorkflowStageViewNode[]> {
  const filter: ApprovalWorkflowStageViewFilterInput | undefined =
    workflowId != null ? { approvalWorkflowId: { eq: workflowId } } : undefined;

  const data = await graphqlRequest<StageListResponse>(STAGE_LIST_QUERY, {
    filter,
    first,
  });
  return data.approvalWorkflows.approvalWorkflowStageViewList.edges.map(
    (e) => e.node,
  );
}

/** Per-tenant KPI summary for the three cards (vw_ApprovalWorkflowKpis). */
export async function getApprovalWorkflowKpisGraphql(): Promise<ApprovalWorkflowKpiViewNode> {
  const data = await graphqlRequest<KpiListResponse>(KPI_LIST_QUERY);
  const node =
    data.approvalWorkflows.approvalWorkflowKpiViewList.edges[0]?.node;
  return node ?? { activeWorkflows: 0, totalStages: 0, namedApprovers: 0 };
}

/** Users eligible to be stage approvers — holders of approve_reject_scenarios (vw_ApprovalWorkflowUsers). */
export async function getApprovalWorkflowUsersGraphql(
  search?: string,
  first = 200,
): Promise<{ userId: string; name: string; email: string }[]> {
  const filter: ApprovalWorkflowUserViewFilterInput | undefined = search
    ? { summary: search }
    : undefined;

  const data = await graphqlRequest<ApproverListResponse>(APPROVER_LIST_QUERY, {
    filter,
    first,
  });
  return data.approvalWorkflows.approvalWorkflowUserViewList.edges.map((e) => ({
    userId: e.node.userId,
    name: e.node.name ?? e.node.userId,
    email: e.node.email ?? "",
  }));
}

/** Scenarios still under review / pending approval within a workflow (vw_WorkflowBlockingScenarios). */
export async function getWorkflowBlockingScenariosGraphql(
  workflowId: number,
  first = 200,
): Promise<
  { id: string; name: string; createdBy: string; createdByName: string }[]
> {
  const filter: WorkflowBlockingScenarioViewFilterInput = {
    approvalWorkflowId: { eq: workflowId },
  };

  const data = await graphqlRequest<BlockingScenarioListResponse>(
    BLOCKING_SCENARIO_LIST_QUERY,
    { filter, first },
  );
  return data.approvalWorkflows.workflowBlockingScenarioViewList.edges.map(
    (e) => ({
      id: String(e.node.scenarioId),
      name: e.node.name,
      createdBy: e.node.createdByUserId,
      createdByName: e.node.createdByUserName ?? e.node.createdByUserId,
    }),
  );
}

/**
 * Folds the (stage, assignee) rows of vw_ApprovalWorkflowStages into the stage shape the
 * Approval Workflows page renders, ordered by stage order.
 */
function buildStages(rows: ApprovalWorkflowStageViewNode[]): WorkflowStage[] {
  const byStageId = new Map<number, WorkflowStage>();

  for (const row of rows) {
    let stage = byStageId.get(row.stageId);
    if (!stage) {
      stage = {
        id: String(row.stageId),
        name: row.name,
        order: row.stageOrder,
        assignees: [],
        rule: (row.approvalRule === "all" ? "all" : "any") as ApprovalRule,
        slaDays: row.slaDays || null,
        escalateToUserId: row.escalateToUserId ?? null,
        escalateToName: row.escalateToName ?? null,
      };
      byStageId.set(row.stageId, stage);
    }
    if (row.assigneeUserId) {
      stage.assignees.push({
        id: String(row.assigneeId ?? `${row.stageId}-${row.assigneeUserId}`),
        type: "named",
        userId: row.assigneeUserId,
        name: row.assigneeName ?? row.assigneeUserId,
        email: row.assigneeEmail ?? "",
      });
    }
  }

  return [...byStageId.values()].sort((a, b) => a.order - b.order);
}

/**
 * The view exposes two persisted flags rather than a status enum; suspension only ever
 * applies to an active workflow.
 */
function deriveStatus(isActive: boolean, isSuspended: boolean): WorkflowStatus {
  if (!isActive) return "inactive";
  return isSuspended ? "suspended" : "active";
}

function toDefinition(
  w: ApprovalWorkflowViewNode,
  stageRows: ApprovalWorkflowStageViewNode[],
): WorkflowDefinition {
  return {
    id: String(w.id),
    name: w.name,
    description: w.description ?? "",
    isDefault: w.isDefault,
    status: deriveStatus(w.isActive, w.isSuspended),
    hasBeenUsed: false,
    allowSelfApproval: w.allowSelfApproval,
    notifyDeactivateActionsCreated: w.notifyDeactivateActionsCreated ?? false,
    routing: {
      minNetImpact: w.minNetImpact != null ? Number(w.minNetImpact) : null,
      maxNetImpact: w.maxNetImpact != null ? Number(w.maxNetImpact) : null,
      categories: w.scenarioCategory ? [w.scenarioCategory] : [],
    },
    stages: buildStages(stageRows.filter((s) => s.approvalWorkflowId === w.id)),
  };
}

/**
 * Stage structure (name, order, rule, assignee display names) for a single workflow,
 * folded from the per-assignee rows of vw_ApprovalWorkflowStages.
 */
export async function getWorkflowStagesGraphql(
  workflowId: number,
): Promise<WorkflowStage[]> {
  return buildStages(await getApprovalWorkflowStageViewsGraphql(workflowId));
}

/**
 * Loads the full workflow list (headers + stages + assignees) for the Approval Workflows
 * page from GraphQL, replacing the previous REST read path. Mutations still go via REST.
 */
export async function getApprovalWorkflowDefinitionsGraphql(): Promise<
  WorkflowDefinition[]
> {
  const [workflows, stageRows] = await Promise.all([
    getApprovalWorkflowViewsGraphql(),
    getApprovalWorkflowStageViewsGraphql(),
  ]);

  return workflows.map((w) => toDefinition(w, stageRows));
}

/**
 * Loads a single workflow (header + stages + assignees) straight from GraphQL so the edit
 * modals always open on fresh data rather than stale in-memory state.
 */
export async function getApprovalWorkflowDefinitionGraphql(
  workflowId: number,
): Promise<WorkflowDefinition | null> {
  const [workflows, stageRows] = await Promise.all([
    getApprovalWorkflowViewsGraphql({ id: { eq: workflowId } }, 1),
    getApprovalWorkflowStageViewsGraphql(workflowId),
  ]);

  const workflow = workflows[0];
  return workflow ? toDefinition(workflow, stageRows) : null;
}
